import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Clock, Calendar, Sun, Moon, Coffee, Wrench, Sparkles, Download } from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { Card, Button, Input, Modal, Badge } from '../components/ui';
import { formatMinutesToHours } from '../lib/utils';
import { toast } from 'sonner';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function Shifts() {
  const { shifts, capacity, fetchShifts, fetchCapacity, createShift, updateShift, deleteShift } = useAppStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    name: '', start_time: '06:00', end_time: '14:30',
    line_setup_min: 30, line_cleanup_min: 30,
    breaks: [{ name: 'Lunch Break', duration_min: 30 }],
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  });

  useEffect(() => { fetchShifts(); fetchCapacity(); }, []);

  const calcNet = (fd) => {
    const [sh, sm] = fd.start_time.split(':').map(Number);
    const [eh, em] = fd.end_time.split(':').map(Number);
    const gross = (eh * 60 + em) - (sh * 60 + sm);
    const totalBreaks = (fd.breaks || []).reduce((s, b) => s + (b.duration_min || 0), 0);
    const nonProd = totalBreaks + (fd.line_setup_min || 0) + (fd.line_cleanup_min || 0);
    return { gross, totalBreaks, nonProd, net: Math.max(0, gross - nonProd) };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      line_setup_min: parseInt(formData.line_setup_min) || 0,
      line_cleanup_min: parseInt(formData.line_cleanup_min) || 0,
      breaks: formData.breaks.map(b => ({ name: b.name, duration_min: parseInt(b.duration_min) || 0 })),
    };
    if (editingShift) { await updateShift(editingShift.id, data); toast.success('Shift updated'); }
    else { await createShift(data); toast.success('Shift created'); }
    setIsModalOpen(false); resetForm();
  };

  const handleEdit = (shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name, start_time: shift.start_time, end_time: shift.end_time,
      line_setup_min: shift.line_setup_min || 0, line_cleanup_min: shift.line_cleanup_min || 0,
      breaks: shift.breaks?.length ? shift.breaks : [{ name: 'Break', duration_min: shift.break_duration_min || 0 }],
      working_days: shift.working_days,
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingShift(null);
    setFormData({ name: '', start_time: '06:00', end_time: '14:30', line_setup_min: 30, line_cleanup_min: 30,
      breaks: [{ name: 'Lunch Break', duration_min: 30 }],
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] });
  };

  const addBreak = () => setFormData(p => ({ ...p, breaks: [...p.breaks, { name: '', duration_min: 15 }] }));
  const removeBreak = (i) => setFormData(p => ({ ...p, breaks: p.breaks.filter((_, idx) => idx !== i) }));
  const updateBreak = (i, field, val) => setFormData(p => ({ ...p, breaks: p.breaks.map((b, idx) => idx === i ? { ...b, [field]: val } : b) }));
  const toggleDay = (day) => setFormData(p => ({ ...p, working_days: p.working_days.includes(day) ? p.working_days.filter(d => d !== day) : [...p.working_days, day] }));

  const fmtMin = (m) => { const h = Math.floor(m / 60); const mm = m % 60; return mm ? `${h}h ${mm}m` : `${h}h`; };

  const exportShifts = () => {
    let txt = 'SHIFT CONFIGURATION REPORT\n';
    txt += `${'='.repeat(60)}\n`;
    txt += `Generated: ${new Date().toLocaleString()}\n\n`;
    for (const s of shifts) {
      const c = calcNet(s);
      txt += `${s.name}\n${'-'.repeat(40)}\n`;
      txt += `  Schedule:     ${s.start_time} - ${s.end_time} (${fmtMin(c.gross)} gross)\n`;
      txt += `  Line Setup:   ${s.line_setup_min || 0} min\n`;
      txt += `  Line Cleanup: ${s.line_cleanup_min || 0} min\n`;
      if (s.breaks?.length) {
        txt += `  Breaks:\n`;
        s.breaks.forEach(b => { txt += `    - ${b.name}: ${b.duration_min} min\n`; });
      }
      txt += `  Total Breaks: ${c.totalBreaks} min\n`;
      txt += `  Non-productive: ${c.nonProd} min\n`;
      txt += `  NET WORK TIME: ${fmtMin(c.net)} (${c.net} min)\n`;
      txt += `  Working Days: ${(s.working_days || []).join(', ')}\n\n`;
    }
    if (capacity) {
      txt += `CAPACITY SUMMARY\n${'='.repeat(60)}\n`;
      txt += `  Daily:   ${capacity.daily_hours}h (${capacity.daily_minutes} min)\n`;
      txt += `  Weekly:  ${capacity.weekly_hours}h (${capacity.weekly_minutes} min)\n`;
      txt += `  Monthly: ${capacity.monthly_hours}h (${capacity.monthly_minutes} min)\n`;
    }
    const blob = new Blob([txt], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `shift-config-${new Date().toISOString().split('T')[0]}.txt`; a.click();
  };

  const stats = calcNet(formData);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Shift Management</h1>
          <p className="text-slate-500 mt-1">Configure working hours, breaks, and calculate net capacity</p>
        </div>
        <div className="flex gap-2">
          {shifts.length > 0 && (
            <Button variant="secondary" onClick={exportShifts} data-testid="export-shifts-btn">
              <Download className="w-4 h-4" /> Export
            </Button>
          )}
          <Button onClick={() => setIsModalOpen(true)} data-testid="add-shift-btn">
            <Plus className="w-4 h-4" /> Add Shift
          </Button>
        </div>
      </div>

      {/* Capacity Overview */}
      {capacity && (
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <h2 className="text-lg font-heading font-semibold mb-4">Production Capacity</h2>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2"><Sun className="w-5 h-5 text-amber-400" /><span className="text-sm text-slate-300">Daily</span></div>
              <p className="text-3xl font-bold font-heading">{capacity.daily_hours}h</p>
              <p className="text-xs text-slate-400">{capacity.daily_minutes} min</p>
            </div>
            <div className="text-center border-x border-slate-700">
              <div className="flex items-center justify-center gap-2 mb-2"><Calendar className="w-5 h-5 text-blue-400" /><span className="text-sm text-slate-300">Weekly</span></div>
              <p className="text-3xl font-bold font-heading">{capacity.weekly_hours}h</p>
              <p className="text-xs text-slate-400">{capacity.weekly_minutes} min</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2"><Moon className="w-5 h-5 text-purple-400" /><span className="text-sm text-slate-300">Monthly</span></div>
              <p className="text-3xl font-bold font-heading">{capacity.monthly_hours}h</p>
              <p className="text-xs text-slate-400">{capacity.monthly_minutes} min</p>
            </div>
          </div>
        </Card>
      )}

      {/* Shifts Detail Cards */}
      {shifts.map(shift => {
        const c = calcNet(shift);
        return (
          <Card key={shift.id} className="overflow-hidden" data-testid={`shift-card-${shift.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg"><Clock className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">{shift.name}</h3>
                  <p className="text-xs text-slate-500">{(shift.working_days || []).length} days/week: {(shift.working_days || []).map(d => d.slice(0,3)).join(', ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(shift)} data-testid={`edit-shift-${shift.id}`}><Edit2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => { if (window.confirm('Delete this shift?')) { deleteShift(shift.id); toast.success('Deleted'); } }} data-testid={`delete-shift-${shift.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>

            {/* Timeline Bar */}
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-sm mb-3">
                <span className="font-mono font-bold text-slate-700">{shift.start_time}</span>
                <div className="flex-1 h-8 bg-slate-200 rounded-full overflow-hidden flex relative">
                  {c.gross > 0 && <>
                    <div className="bg-amber-400 h-full" style={{ width: `${((shift.line_setup_min || 0) / c.gross) * 100}%` }} title={`Setup: ${shift.line_setup_min}min`} />
                    <div className="bg-emerald-500 h-full flex-1" title={`Net Work: ${c.net}min`} />
                    {(shift.breaks || []).map((b, i) => (
                      <div key={i} className="bg-orange-400 h-full" style={{ width: `${(b.duration_min / c.gross) * 100}%` }} title={`${b.name}: ${b.duration_min}min`} />
                    ))}
                    <div className="bg-blue-400 h-full" style={{ width: `${((shift.line_cleanup_min || 0) / c.gross) * 100}%` }} title={`Cleanup: ${shift.line_cleanup_min}min`} />
                  </>}
                </div>
                <span className="font-mono font-bold text-slate-700">{shift.end_time}</span>
              </div>
              <div className="flex gap-4 text-[11px] flex-wrap">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> Setup ({shift.line_setup_min || 0}m)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Net Work ({c.net}m)</span>
                {(shift.breaks || []).map((b, i) => (
                  <span key={i} className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400" /> {b.name} ({b.duration_min}m)</span>
                ))}
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-400" /> Cleanup ({shift.line_cleanup_min || 0}m)</span>
              </div>
            </div>

            {/* Summary Grid */}
            <div className="grid grid-cols-5 gap-3 text-center">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 mb-1">Gross Time</p>
                <p className="font-bold text-slate-800">{fmtMin(c.gross)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-[10px] text-amber-600 mb-1">Setup</p>
                <p className="font-bold text-amber-700">{shift.line_setup_min || 0}m</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-[10px] text-orange-600 mb-1">Breaks</p>
                <p className="font-bold text-orange-700">{c.totalBreaks}m</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-[10px] text-blue-600 mb-1">Cleanup</p>
                <p className="font-bold text-blue-700">{shift.line_cleanup_min || 0}m</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 ring-2 ring-emerald-200">
                <p className="text-[10px] text-emerald-600 mb-1">NET WORK</p>
                <p className="font-bold text-emerald-700 text-lg">{fmtMin(c.net)}</p>
              </div>
            </div>
          </Card>
        );
      })}

      {shifts.length === 0 && <Card><p className="text-center text-slate-400 py-8">No shifts configured. Add your first shift to calculate capacity.</p></Card>}

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); resetForm(); }} title={editingShift ? 'Edit Shift' : 'Add Shift'}>
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <Input label="Shift Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Morning Shift" required data-testid="shift-name-input" />
          
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start Time" type="time" value={formData.start_time} onChange={e => setFormData({ ...formData, start_time: e.target.value })} required data-testid="shift-start-input" />
            <Input label="End Time" type="time" value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} required data-testid="shift-end-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><Wrench className="w-3.5 h-3.5 text-amber-500" /> Line Setup (min)</label>
              <input type="number" min="0" value={formData.line_setup_min} onChange={e => setFormData({ ...formData, line_setup_min: parseInt(e.target.value) || 0 })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="shift-setup-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-blue-500" /> Line Cleanup (min)</label>
              <input type="number" min="0" value={formData.line_cleanup_min} onChange={e => setFormData({ ...formData, line_cleanup_min: parseInt(e.target.value) || 0 })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="shift-cleanup-input" />
            </div>
          </div>

          {/* Breaks Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1"><Coffee className="w-3.5 h-3.5 text-orange-500" /> Breaks</label>
              <button type="button" onClick={addBreak} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="add-break-btn">+ Add Break</button>
            </div>
            <div className="space-y-2">
              {formData.breaks.map((b, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input type="text" value={b.name} onChange={e => updateBreak(i, 'name', e.target.value)} placeholder="Break name"
                    className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm" data-testid={`break-name-${i}`} />
                  <input type="number" min="0" value={b.duration_min} onChange={e => updateBreak(i, 'duration_min', parseInt(e.target.value) || 0)}
                    className="w-20 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-right" data-testid={`break-duration-${i}`} />
                  <span className="text-xs text-slate-400">min</span>
                  <button type="button" onClick={() => removeBreak(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Working Days */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Working Days</label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(day => (
                <button key={day} type="button" onClick={() => toggleDay(day)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${formData.working_days.includes(day) ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`day-${day.toLowerCase()}`}>{day.slice(0, 3)}</button>
              ))}
            </div>
          </div>

          {/* Live Preview */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs font-medium text-emerald-700 mb-2">Preview</p>
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div><p className="text-slate-500">Gross</p><p className="font-bold">{fmtMin(stats.gross)}</p></div>
              <div><p className="text-slate-500">Non-productive</p><p className="font-bold text-red-600">-{stats.nonProd}m</p></div>
              <div><p className="text-slate-500">Net Work</p><p className="font-bold text-emerald-700 text-lg">{fmtMin(stats.net)}</p></div>
              <div><p className="text-slate-500">Weekly</p><p className="font-bold">{fmtMin(stats.net * formData.working_days.length)}</p></div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setIsModalOpen(false); resetForm(); }}>Cancel</Button>
            <Button type="submit" data-testid="shift-submit-btn">{editingShift ? 'Update' : 'Add'} Shift</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
